// ============================================================================
// PARTICIPANT SERVICE
// API client for the mobile participant view.
// Uses HttpOnly session cookie after claim — no Authorization header needed.
// ============================================================================

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const TOKEN_KEY = 'participant_token';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  Object.assign(headers, options.headers || {});
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers,
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(new Error(body.error || `HTTP ${res.status}`), { status: res.status, body });
  }
  return res.json();
}

export interface JoinInfo {
  participantId: string;
  sessionId: string;
  sessionName: string;
  claimed: boolean;
}

export interface ParticipantAvatar {
  id: string;
  name: string;
  role: string;
  status: string;
  imageUrl?: string;
  primaryColor?: string;
}

export interface PendingContributionItem {
  role: string;
  avatarName: string;
  avatarImageUrl?: string;
  avatarPrimaryColor?: string;
  text: string;
  reason?: string;
  urgency?: 'urgent' | 'important' | 'normal';
  audioUrl?: string;
}

export interface PublishedOutput {
  id: string;
  title: string;
  type: string;
  body: string;
  url?: string;
  publishedAt: string;
}

export interface PublishedAsset {
  id: string;
  name: string;
  type: string;
  url: string;
  publishedAt: string;
}

export interface ActiveWidget {
  widgetId: string;
  type: 'mcq' | 'free_text' | 'rating';
  question: string;
  options?: string[];
  ratingMax?: number;
  submittedAnswer?: string | null;
}

export interface SessionState {
  sessionName: string;
  phase: string;
  phaseIndex: number;
  totalPhases: number;
  avatars: ParticipantAvatar[];
  pendingContributions: PendingContributionItem[];
  publishedOutputs: PublishedOutput[];
  publishedAssets: PublishedAsset[];
  activeWidget: ActiveWidget | null;
  reachy: { available: boolean; busy: boolean };
  guide: { name: string; enabled: boolean };
}

export const participantService = {
  async getJoinInfo(token: string): Promise<JoinInfo> {
    return request(`/api/participant/join?token=${encodeURIComponent(token)}`);
  },

  async join(token: string, name: string): Promise<{ success: boolean; sessionId: string; name: string }> {
    const result = await request<{ success: boolean; sessionId: string; name: string; token?: string }>(
      '/api/participant/join',
      { method: 'POST', body: JSON.stringify({ token, name }) }
    );
    if (result.token) sessionStorage.setItem(TOKEN_KEY, result.token);
    return result;
  },

  async getState(sessionId: string): Promise<SessionState> {
    return request(`/api/participant/${sessionId}/state`);
  },

  async sendChat(sessionId: string, message: string, avatarId?: string): Promise<{ messageId: string; response: string }> {
    return request(`/api/participant/${sessionId}/chat`, {
      method: 'POST',
      body: JSON.stringify({ message, avatarId }),
    });
  },

  async converse(sessionId: string): Promise<{ success: boolean }> {
    return request(`/api/participant/${sessionId}/converse`, { method: 'POST' });
  },

  async submitWidgetResponse(sessionId: string, widgetId: string, answer: string): Promise<{ success: boolean }> {
    return request(`/api/participant/${sessionId}/widget-response`, {
      method: 'POST',
      body: JSON.stringify({ widgetId, answer }),
    });
  },
};
